// Shared, pure helpers behind the profile activity views. Kept out of the
// components so the week strip, the year grid and the last read copy can be
// tested without rendering. Mirrors redwing-app/src/features/profile/helpers.
export const MONTH_LABELS = [
  "J",
  "F",
  "M",
  "A",
  "M",
  "J",
  "J",
  "A",
  "S",
  "O",
  "N",
  "D",
];

const WEEK_DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const BLOCKS_PER_MONTH = 5;
const DAYS_PER_BLOCK = 7;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const pad = (value) => String(value).padStart(2, "0");

// Local calendar day, not UTC: the API keys its activity by the user's own
// timezone, so shifting to UTC here would move reads onto the wrong day.
export const dateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const keyFor = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Monday first, matching the M T W T F S S strip on the profile.
const startOfWeek = (date) => {
  const start = startOfDay(date);
  const weekday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - weekday);
  return start;
};

export const levelFor = (count) => {
  if (count > 10) return 4;
  if (count > 5) return 3;
  if (count > 1) return 2;
  if (count === 1) return 1;
  return 0;
};

/**
 * The current calendar week, Monday to Sunday. Days that have not happened yet
 * (today included, until something is read) are flagged so the strip can show
 * them as an outline instead of a filled cell.
 */
export const buildWeekStrip = (counts = {}, now = new Date()) => {
  const today = startOfDay(now);
  const monday = startOfWeek(now);

  return WEEK_DAY_LABELS.map((label, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    const key = dateKey(date);
    const count = Number(counts[key] || 0);

    return {
      key,
      label,
      count,
      isToday: date.getTime() === today.getTime(),
      isUpcoming: date.getTime() >= today.getTime() && !count,
    };
  });
};

/**
 * One column per month, each column a stack of week blocks. Aggregating days
 * into blocks keeps the whole year readable without scrolling.
 */
export const buildYearPixels = (year, counts = {}) =>
  MONTH_LABELS.map((label, month) => {
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const blocks = Array.from({ length: BLOCKS_PER_MONTH }, (_, block) => {
      const firstDay = block * DAYS_PER_BLOCK + 1;
      const lastDay = Math.min(firstDay + DAYS_PER_BLOCK - 1, daysInMonth);
      let count = 0;

      for (let day = firstDay; day <= lastDay; day += 1) {
        count += Number(counts[keyFor(year, month, day)] || 0);
      }

      return {
        key: `${year}-${month}-${block}`,
        count,
        firstDay,
        lastDay,
        isOutside: firstDay > daysInMonth,
      };
    });

    return { label, index: month, blocks };
  });

/**
 * "Today" / "Yesterday" for the last read row, falling back to a plain date
 * once the read is older than a week.
 */
export const formatLastReadDay = (value, now = new Date()) => {
  if (!value) return null;

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;

  const days = Math.round(
    (startOfDay(now).getTime() - startOfDay(date).getTime()) / DAY_IN_MS
  );

  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;

  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * "Civil War · 12 of 137" when we know the reading order the comic came from.
 */
export const formatLastReadDetail = (order, fallback = "Comic") => {
  const title = order?.title || fallback;
  const totalComics = Number(order?.totalComics) || 0;
  const totalRead = Number(order?.totalRead) || 0;

  if (!order?.title || !totalComics) return title;

  return `${title} · ${totalRead} of ${totalComics}`;
};
